import * as braze from "@braze/web-sdk";
import loadingText from "assets/gif/loadingtext.json";
import dropin from "braintree-web-drop-in";
import branch from "branch-sdk";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ImageSourcePropType } from "react-native";
import { batch, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { store } from "redux_store/configureReduxStore";

import config from "config";
import {
  AnalyticsAction,
  AnalyticsConst,
  AnalyticsInstance,
  AnalyticsPayloadGenerator,
  BraintreeReduxActions,
  BranchConst,
  CheckoutAction,
  CheckoutModels,
  cleanUpOrder,
  LoyaltyActions,
  OrderModules,
  OrderReduxAction,
  RootState,
  SentryLoggerInstance,
} from "gyg_common";
import reloadOrderText from "gyg_common/components/assets/gif/reloadOrderText.json";
import questionImage from "gyg_common/components/assets/images/illustration_error_question.png";
import LoadingScreen from "gyg_common/components/LoadingScreen";
import ModalWithButton from "gyg_common/components/modals/ModalWithButton";
import { CheckoutErrorModal } from "gyg_common/components/modals/NonAsyncCheckoutFlow/CheckoutErrorModal";
import useHandleOrderError, {
  isOrderProccessing,
} from "gyg_common/hooks/NonAsyncCheckoutFlow/useHandleOrderError";
import useCreateUpdateOrder from "gyg_common/hooks/useCreateUpdateOrder";
import { getOrderIdInProgressValueFromLocalStorage } from "gyg_common/services/api/checkout";
import { Screens } from "navigation/const";
import { Payment } from "views/components/Payment";

const GOOGLE_MERCHANT_ID = config.googleMerchantId;
const PATCH_ORDER_ANIMATION_HIDE_DELAY = 1000;

const PaymentContainer: React.FC = () => {
  // hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isFocused = pathname === Screens.Payment;

  // redux state
  const {
    clientTokenResponse,
    paymentSuccess,
    getClientTokenLoading,
    isSavePayment,
    error: checkoutError,
    paymentResponse,
    loading: checkoutLoading,
  } = useSelector((s: RootState) => s.checkout);
  const { firstName, lastName, email, phoneNumber } = useSelector(
    (s: RootState) => s.guest
  );
  const {
    includeCutlery,
    getOrderLoading,
    getOrderResponse,
    getOrderError,
    orderASAP,
    orderTime,
    orderTimeUpdated,
    updateOrderLoading,
  } = useSelector((s: RootState) => s.order);
  const { loyalty, redeemDollars, activeReward } = useSelector(
    (s: RootState) => s.loyalty
  );
  const { selectedStore } = useSelector((s: RootState) => s.store);
  const { isAuthSuccess } = useSelector((s: RootState) => s.login);
  const { profile } = useSelector((s: RootState) => s.user);
  const basket = useSelector(
    (s: RootState) => s.order.getOrderResponse?.basket
  );
  const { paymentPayload: savedPaymentPayload } = useSelector(
    (s: RootState) => s.braintree
  );
  const { orderStatus, delivery } = useSelector(
    (state: RootState) => state.order
  );
  const { isTimeout } = useSelector((s: RootState) => s.braintree);
  const analyticsState = useSelector((s: RootState) => s.analytics);
  const { isBrazeInitialised } = useSelector((s: RootState) => s.user);

  const { createOrder, updateOrder } = useCreateUpdateOrder(
    store,
    pathname,
    false
  );

  const isCheckoutInProgressOrSuccess =
    checkoutLoading || (paymentResponse && paymentSuccess);

  // variables
  const items = basket?.basketItems || [];
  const total = basket?.total ?? 0;
  const fullLoyaltyPayment = isAuthSuccess && total === 0;
  const clientToken = clientTokenResponse.clientToken;
  const supportedNetworks = [
    "amex",
    "discover",
    "maestro",
    "masterCard",
    "visa",
  ];

  const dropInObject: dropin.Options = {
    authorization: clientToken,
    container: "#dropin-container",
    vaultManager: true,
    paypal: {
      flow: "checkout",
      amount: total.toFixed(2),
      currency: t("CheckoutPayment:currencyCode"),
      commit: true,
    },
    googlePay: {
      googlePayVersion: 2,
      merchantId: GOOGLE_MERCHANT_ID,
      transactionInfo: {
        totalPriceStatus: "FINAL",
        totalPrice: total.toFixed(2),
        currencyCode: t("CheckoutPayment:currencyCode"),
      },
    },
    applePay: {
      displayName: t("CheckoutPayment:storeLabel"),
      paymentRequest: {
        countryCode: config.version,
        supportedNetworks: supportedNetworks,
        currencyCode: t("CheckoutPayment:currencyCode"),
        merchantCapabilities: [
          "supports3DS",
          "supportsCredit",
          "supportsDebit",
        ],
        total: {
          label: t("CheckoutPayment:storeLabel"),
          amount: total.toFixed(2),
        },
        requiredBillingContactFields: ["postalAddress"],
        requiredShippingContactFields: ["postalAddress"],
        shippingContact: ["postalAddress"],
      },
    },
  };

  // local state
  const [recaptchaCode, setRecaptchaCode] = useState<string | null>();
  const [instance, setInstance] = useState<dropin.Dropin>();
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentOrderId, setCurrentOrderId] = useState(
    getOrderResponse?.orderId
  );
  // flag to skip createUpdateOrder call to prevent
  // the PATCH api call to updateOrderById when payment via
  // BrainTree gateway fails
  const [isSkipCreateUpdateOrder, setIsSkipCreateUpdateOrder] = useState(false);
  const [retryFetchingOrderStatus, setRetryFetchingOrderStatus] =
    useState(false);
  const [isPatchOrdering, setIsPatchOrdering] = useState(false);
  const isPaymentErrorRetry = useRef(false);
  const isPaymentSubmitted = useRef(false);
  const isOrderFailed = useRef(false);
  const shouldSubmitOrderAfterCreate = useRef<boolean>(false);
  const initIssieOfBraintreeDropUI = useRef<boolean>(false);
  const captchaExpired = useRef<boolean>(false);

  const loadingAnimation = useMemo(() => {
    if (
      (getOrderLoading || checkoutLoading || isPatchOrdering) &&
      isPaymentErrorRetry.current
    ) {
      return reloadOrderText;
    } else if (
      checkoutLoading ||
      ((getOrderLoading || isPatchOrdering) && isPaymentSubmitted.current)
    ) {
      return loadingText;
    } else {
      return undefined;
    }
  }, [checkoutLoading, getOrderLoading, isPatchOrdering]);

  const onExpired = useCallback(() => {
    captchaExpired.current = true;
  }, []);

  /**
   * Triggers payment action.
   * @param payload
   */
  const initPayment = useCallback(
    (payload: CheckoutModels.PaymentPayload) => {
      if (payload && selectedStore) {
        dispatch(
          BraintreeReduxActions.savePaymentPayload({
            paymentPayload: payload,
            store: selectedStore,
          })
        );
        isPaymentSubmitted.current = true;
        dispatch(
          isAuthSuccess
            ? CheckoutAction.initPaymentAuthorized(payload)
            : CheckoutAction.initPayment(payload)
        );
        if (!isLoading) {
          setIsLoading(true);
        }
      }
    },
    [dispatch, isAuthSuccess, isLoading, selectedStore]
  );

  const resetOrderErrorHandlerStates = useCallback(() => {
    setRetryFetchingOrderStatus(false);
    isPaymentSubmitted.current = false;
    isPaymentErrorRetry.current = false;
  }, []);

  /**
   * Triggers braintree and prepares payload for payment.
   */
  const proceedWithPayment = useCallback(
    (onRetry?: boolean) => {
      if (captchaExpired.current) {
        captchaExpired.current = false;
        return;
      }

      setIsLoading(false);
      const payment: CheckoutModels.Payment = {};
      const customer: CheckoutModels.Customer = {
        firstName: isAuthSuccess ? profile?.firstName ?? "" : firstName,
        lastName: isAuthSuccess ? profile?.lastName ?? "" : lastName,
        emailAddress: isAuthSuccess ? profile?.email ?? "" : email,
        phoneNumber: isAuthSuccess ? profile?.phoneNumber : phoneNumber,
      };

      const newPayload: CheckoutModels.PaymentPayload = {
        id: currentOrderId ?? "",
        customer: customer,
        payment: payment,
        addCutlery: includeCutlery,
      };

      if (activeReward?.userCouponCode) {
        payment.coupon = {
          code: activeReward.userCouponCode,
        };
      }

      if (redeemDollars && loyalty?.cardNumber) {
        payment.gygDollar = {
          cardNumber: loyalty.cardNumber,
          amount: redeemDollars,
        };
      }

      if (!currentOrderId?.length && !onRetry) {
        // Edge case when order ID doesn't return
        // throw generic error as we do not know what
        // happened to the order
        SentryLoggerInstance.sentryCaptureCustomError(
          "[WEB]PaymentContainer: Order ID not available",
          {}
        );
        resetOrderErrorHandlerStates();
        dispatch(CheckoutAction.setHasUnexpectedError(true));
      } else if (onRetry === true && savedPaymentPayload) {
        isPaymentSubmitted.current = true;
        dispatch(
          isAuthSuccess
            ? CheckoutAction.initPaymentAuthorized(savedPaymentPayload)
            : CheckoutAction.initPayment(savedPaymentPayload)
        );
      } else if (total) {
        if (instance) {
          instance.requestPaymentMethod((braintreeError, payload) => {
            if (braintreeError) {
              SentryLoggerInstance.sentryBraintreeError(braintreeError);
            }
            //proceeds with payment
            if (payload) {
              payment.braintree = {
                checkoutType: 0,
                nonce: payload.nonce,
                amount: total.toFixed(2),
                storeCard: isSavePayment,
                paymentToken: clientToken,
                bTAFT: payload.deviceData || "",
              };
              dispatch(AnalyticsAction.setPaymentType(payload.type));
              initPayment(newPayload);
            } else {
              //clear all instance and get new client token
              if (initIssieOfBraintreeDropUI.current) {
                return;
              }
              initIssieOfBraintreeDropUI.current = true;
              instance.teardown();
              setInstance(undefined);
              if (currentOrderId) {
                dispatch(
                  CheckoutAction.getClientToken({
                    orderId: currentOrderId,
                  })
                );
              }
            }
          });
        } else {
          if (currentOrderId) {
            dispatch(
              CheckoutAction.getClientToken({
                orderId: currentOrderId,
              })
            );
          }
        }
      } else {
        if (payment) {
          initPayment(newPayload);
        }
      }
    },
    [
      isAuthSuccess,
      profile?.firstName,
      profile?.lastName,
      profile?.email,
      profile?.phoneNumber,
      firstName,
      lastName,
      email,
      phoneNumber,
      currentOrderId,
      includeCutlery,
      activeReward?.userCouponCode,
      redeemDollars,
      loyalty?.cardNumber,
      savedPaymentPayload,
      total,
      resetOrderErrorHandlerStates,
      dispatch,
      instance,
      isSavePayment,
      clientToken,
      initPayment,
    ]
  );

  const confirmWarningModal = () => {
    setShowWarning(false);
    if (
      OrderModules.OrderUtils.isOrderTimeAsapExpired(orderTime, orderASAP) &&
      getOrderResponse
    ) {
      updateOrder(currentOrderId);
      if (currentOrderId) {
        setIsLoading(true);
      }
    }
  };

  const goBack = () => {
    if (showWarning) {
      setShowWarning(false);
    }
    history.push(Screens.Order);
  };

  const recreateOrder = useCallback(() => {
    setIsSkipCreateUpdateOrder(true);
    batch(() => {
      // clear order response and order ID so a new order can be created
      dispatch(OrderReduxAction.clearOrderResponse());
      // reset payment status
      dispatch(CheckoutAction.resetPayment());
      // reset the braintree payment payload
      dispatch(BraintreeReduxActions.clearPaymentPayload());
      dispatch(CheckoutAction.resetPayment());
    });

    isOrderFailed.current = false;
    isPaymentSubmitted.current = false;
    shouldSubmitOrderAfterCreate.current = true;

    // create a new order
    createOrder();
  }, [createOrder, dispatch]);

  const clearOrderErrorStates = useCallback(() => {
    dispatch(OrderReduxAction.clearOrderError());
    dispatch(CheckoutAction.resetPayment());
    dispatch(BraintreeReduxActions.setTimeoutError(false));
  }, [dispatch]);

  const runRetryFetchingOrderStatus = useCallback(() => {
    clearOrderErrorStates();
    isPaymentSubmitted.current = false;
    isPaymentErrorRetry.current = true;
    setRetryFetchingOrderStatus(true);
  }, [clearOrderErrorStates]);

  const onDismissErrorModal = () => {
    setIsSkipCreateUpdateOrder(true);
    if (isOrderProccessing(orderStatus)) {
      /**
       * If there is order status (received after order handler pass), check if order received on BE,
       * (possible situation if order status is success but checkout endpoint is still failing or timeout again)
       * we need to clear current order and move user to home screen to avoid creating duplicate order,
       * current order will appear on Home screen on current orders sections
       */
      cleanUpOrder(dispatch, orderStatus.orderId);
      history.push(Screens.Dashboard);
      if (isAuthSuccess) {
        setTimeout(() => {
          dispatch(LoyaltyActions.getUserLoyalty());
          dispatch(LoyaltyActions.getUserRewards());
          dispatch(
            LoyaltyActions.getUserCoffeeLoyalty(getOrderResponse?.orderId)
          );
        }, 1000); //add timeout to make sure data is refreshed on home screen and show current order
      }
    } else if (
      currentOrderId &&
      !isOrderFailed.current &&
      isPaymentSubmitted.current &&
      checkoutError?.title !== "BraintreeTransactionCreationFailedException"
    ) {
      /**
       *  fetch order data to make sure order wasn't accepted by BE to avoid duplicate order
       *  call this error handler only if order was submitted already "isPaymentSubmitted == true"
       *  and only if there was no payment(Braintree) failure
       */
      clearOrderErrorStates();
      runRetryFetchingOrderStatus();
    } else {
      // order has not been accepted, recreate order
      // recreates for Braintree soft/hard errors encountered
      clearOrderErrorStates();
      recreateOrder();
    }
  };

  /**
   * Checks if applied rewards value is above the order value.
   */
  const onPayNow = () => {
    if (paymentSuccess) {
      //Move to Order status screen if checkout response exist
      history.push(Screens.OrderStatus, {
        previousScreen: pathname,
      });
      return;
    }
    setIsSkipCreateUpdateOrder(false);
    initIssieOfBraintreeDropUI.current = false;

    if (isPaymentSubmitted.current) {
      onDismissErrorModal();
    } else {
      const isLocalOrderTimeExpired =
        OrderModules.OrderUtils.isOrderTimeAsapExpired(orderTime, orderASAP);
      const isOrderPickUpTimeExpired = getOrderResponse?.pickUpTime
        ? OrderModules.OrderUtils.isOrderTimeAsapExpired(
            new Date(getOrderResponse.pickUpTime).getTime(),
            true
          )
        : false;
      const shouldUpdateTime =
        (isLocalOrderTimeExpired || isOrderPickUpTimeExpired) &&
        getOrderResponse &&
        !isSkipCreateUpdateOrder;

      if (shouldUpdateTime) {
        updateOrder(currentOrderId, true);
        if (currentOrderId) {
          setIsLoading(true);
        }
      } else if (
        //TODO: Do we really need this validation and modal window?
        getOrderResponse?.basket?.total &&
        getOrderResponse?.basket?.total < 0
      ) {
        setShowWarning(true);
      } else {
        proceedWithPayment();
      }
    }
  };

  const onHandleOrderFailure = useCallback(
    (forceRecreateOrder: boolean) => {
      isOrderFailed.current = true;
      resetOrderErrorHandlerStates();
      if (forceRecreateOrder) {
        recreateOrder();
      }
    },
    [recreateOrder, resetOrderErrorHandlerStates]
  );

  //TODO:wrap this function in useCallback once the useEffect in useHandleOrderError has been fixed
  const onHandleOrderSuccess = (recreateAndSubmitOrder?: boolean) => {
    resetOrderErrorHandlerStates();
    if (recreateAndSubmitOrder === true) {
      shouldSubmitOrderAfterCreate.current = true;
      createOrder();
    } else {
      proceedWithPayment(true);
    }
  };

  useHandleOrderError({
    orderId: currentOrderId,
    isProcessing: retryFetchingOrderStatus,
    onHandleFailure: onHandleOrderFailure,
    onHandleSuccess: onHandleOrderSuccess,
  });

  /**
   * Analytics view
   */
  useEffect(() => {
    if (isBrazeInitialised) {
      AnalyticsInstance.trackView({
        page_name: "checkout_details",
        page_type: "checkout_details_view",
      });
    }
  }, [isBrazeInitialised]);

  useEffect(() => {
    if (!getClientTokenLoading && clientToken) {
      dropin.create(dropInObject, (_, dropInInstance) => {
        // only set the dropin if it exists
        // on payment error, this instance is undefined
        if (dropInInstance) {
          setInstance(dropInInstance);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientToken, getClientTokenLoading]);

  useEffect(() => {
    if (paymentSuccess && paymentResponse) {
      shouldSubmitOrderAfterCreate.current = false;
      setIsLoading(false);
      branch.logEvent(
        BranchConst.BranchEvents.Purchase,
        {
          branchId: profile?.branchId ?? "",
        },
        items
      );

      //track user custom attributes only for logged in users
      if (selectedStore?.name && isAuthSuccess) {
        AnalyticsInstance.setCustomAttribute(
          AnalyticsConst.CustomAttribute.LastStoreOrdered,
          selectedStore.name
        );
      }

      AnalyticsInstance.trackEvent(
        AnalyticsConst.Events.PayOrder,
        AnalyticsPayloadGenerator.payOrderPayload(
          analyticsState,
          AnalyticsConst.DeviceSource.WEB
        )
      );
      isPaymentSubmitted.current = false;
      braze.logPurchase(
        paymentResponse.order.orderId,
        paymentResponse.order.basket.total,
        config.version === "AU" ? "AUD" : "USD",
        paymentResponse.order.basket.basketItems.length
      );

      //TODO: Update redirect to Order Progress screen (Async checkout flow)
      history.push(Screens.OrderStatus, {
        previousScreen: pathname,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSuccess, history, paymentResponse]);

  useEffect(() => {
    if (
      (!getOrderLoading &&
        !isPatchOrdering &&
        getOrderResponse &&
        getOrderResponse.basket.errors) ||
      (getOrderError && !retryFetchingOrderStatus)
    ) {
      // navigate back to order view
      history.push(Screens.Order);
      setIsLoading(false);
      shouldSubmitOrderAfterCreate.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getOrderResponse, getOrderError, getOrderLoading, isPatchOrdering]);

  //update redeemGyg value
  useEffect(() => {
    if (
      getOrderResponse?.gygDollars?.applied !== undefined &&
      getOrderResponse?.gygDollars?.applied !== redeemDollars
    ) {
      dispatch(
        LoyaltyActions.updateRedeemDollars(
          getOrderResponse?.gygDollars?.applied
        )
      );
    }
    if (getOrderResponse) {
      setCurrentOrderId(getOrderResponse?.orderId);
    }
  }, [dispatch, getOrderResponse, redeemDollars]);

  /**
   * Proceed with order after recreate
   */
  useEffect(() => {
    if (
      shouldSubmitOrderAfterCreate.current === true &&
      getOrderResponse &&
      clientTokenResponse.clientToken.length > 0 &&
      !isCheckoutInProgressOrSuccess
    ) {
      shouldSubmitOrderAfterCreate.current = false;
      if (savedPaymentPayload) {
        proceedWithPayment(true);
      } else {
        proceedWithPayment();
      }
    }
  }, [
    clientTokenResponse,
    getOrderResponse,
    isCheckoutInProgressOrSuccess,
    proceedWithPayment,
    savedPaymentPayload,
  ]);

  useEffect(() => {
    if (
      !OrderModules.OrderUtils.isCheckoutInProgressError(checkoutError) &&
      checkoutError
    ) {
      setIsLoading(false);
      AnalyticsInstance.trackEvent(AnalyticsConst.Events.PaymentError, {
        error_message: checkoutError.message,
      });
    } else if (
      OrderModules.OrderUtils.isCheckoutInProgressError(checkoutError)
    ) {
      //fetch order status on 421 error to get correct order status
      runRetryFetchingOrderStatus();
    }
  }, [checkoutError, runRetryFetchingOrderStatus]);

  useEffect(() => {
    if (
      orderTimeUpdated &&
      getOrderResponse &&
      !getOrderLoading &&
      !checkoutLoading &&
      isFocused &&
      !isCheckoutInProgressOrSuccess
    ) {
      proceedWithPayment();
      //reset orderTimeUpdated to false
      dispatch(OrderReduxAction.clearOrderError());
    }
  }, [
    orderTimeUpdated,
    getOrderResponse,
    proceedWithPayment,
    dispatch,
    getOrderLoading,
    checkoutLoading,
    isFocused,
    isCheckoutInProgressOrSuccess,
  ]);

  /**
   * If checkout wasn't submitted move user to Dashboard but do not clear basket and order data, if checkout was submitted look for
   * order id stored to local storage and run order stats fetch process, once success status received move user to Order Status
   */
  useEffect(() => {
    if (!selectedStore) {
      const currentOrderIdFromLocalStorage =
        getOrderIdInProgressValueFromLocalStorage() ?? "";
      if (currentOrderIdFromLocalStorage.length > 0) {
        setCurrentOrderId(currentOrderIdFromLocalStorage);
        runRetryFetchingOrderStatus();
      } else {
        history.push(Screens.Dashboard);
      }
    }
  }, [dispatch, history, runRetryFetchingOrderStatus, selectedStore]);

  /** Adds delay after patch order response came to avoid user tap Pay Now button in delay of animations changes */
  useEffect(() => {
    if (!updateOrderLoading) {
      setTimeout(() => {
        setIsPatchOrdering(false);
      }, PATCH_ORDER_ANIMATION_HIDE_DELAY);
    } else {
      setIsPatchOrdering(true);
    }
  }, [updateOrderLoading]);

  const listenReloadActions = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    // Chrome requires returnValue to be set.
    event.returnValue = "";
  };

  /** Prevent user to move from screen if checkout loading */
  const listenToPopActions = () => {
    if ((checkoutLoading || getOrderLoading || isPatchOrdering) && isFocused) {
      history.go(1);
    }
  };

  /** Listen to navigation changes and prevent user to move from screen or refresh the screen without popup confirmation */
  useEffect(() => {
    window.addEventListener("beforeunload", listenReloadActions);
    window.addEventListener("popstate", listenToPopActions);
    return () => {
      window.removeEventListener("beforeunload", listenReloadActions);
      window.removeEventListener("popstate", listenToPopActions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <LoadingScreen
        loading={
          (checkoutLoading ||
            isLoading ||
            getClientTokenLoading ||
            getOrderLoading ||
            isPatchOrdering) &&
          !isTimeout
        }
        json={loadingAnimation}
        description={
          (checkoutLoading || getOrderLoading || isPatchOrdering) &&
          isPaymentSubmitted.current
            ? t("OrderManagement:orderProcessingDescriptionWeb")
            : undefined
        }
      />
      <CheckoutErrorModal onModalDismiss={onDismissErrorModal} />
      <Payment
        order={getOrderResponse}
        fullLoyaltyPayment={fullLoyaltyPayment}
        isDisabled={!recaptchaCode}
        onGoogleReCAPTCHA={setRecaptchaCode}
        onGoogleReCAPTCHAExpired={onExpired}
        onPayClick={onPayNow}
        isLoading={isLoading}
        deliveryFee={delivery?.fee}
      />
      <ModalWithButton
        isVisible={showWarning}
        small={true}
        title={t("Loyalty:loyaltyWarningTitle")}
        messageBold={t("Loyalty:loyaltyWarningCopy")}
        primaryButton={{
          name: t("Loyalty:loyaltyWarningConfirm"),
          action: confirmWarningModal,
        }}
        secondaryButton={{
          name: t("Loyalty:loyaltyWarningBack"),
          action: goBack,
        }}
        image={questionImage as ImageSourcePropType}
        onModalClose={() => setShowWarning(false)}
        modalId='reward-warning-moodal'
      />
    </>
  );
};

export default PaymentContainer;
